import React, { useEffect, useState } from "react"
import Pagination from "../../components/Pagination"
import { FiPlusCircle, FiEye, FiCalendar, FiClock, FiUser } from "react-icons/fi"
import { SuccessNotification } from "../../utils"
import { Redirect, useLocation } from "react-router-dom"
import DashboardHeader from "../../components/DashboardHeader"
import { Tooltip } from "../../layout/sidebar"
import { IoArrowBack, IoArrowForward } from "react-icons/io5"
import Spinner from "../../components/Spinner"
import useSWR from "swr"
import { BASE_URL, fetcher, fetchAll } from "../../utils/api"
import { RxPencil1 } from "react-icons/rx"
import DeleteModal from "../../components/DeleteModal"
import { useHistory } from "react-router-dom"
import { BsTrash2 } from "react-icons/bs"

const PAGE_SIZE = 10

const AppointmentView = () => {
  const history = useHistory()
  const [currentPage, setCurrentPage] = useState(1)
  const [selected, setSelected] = useState(null)
  const [q, setQ] = useState("")
  const [states, setState] = useState({ status: "", date: "" })
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection"
    }
  ])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { data: appointments, isLoading, mutate } = useSWR(
    `appointments/`,
    () => fetchAll(`appointments/`)
  )

  // --- Filtering and Search Logic ---
  const getFilteredAppointments = () => {
    let data = appointments || []

    // Filter by status
    if (states.status && states.status !== "") {
      data = data?.filter(
        (el) => el.status?.toLowerCase() === states.status
      )
    }

    // Date range filter
    const startDate = dateRange[0]?.startDate
    const endDate = dateRange[0]?.endDate
    if (startDate && endDate) {
      data = data?.filter(
        (el) => {
          const appointmentDate = new Date(el.date)
          return appointmentDate >= startDate && appointmentDate <= endDate
        }
      )
    }

    // Search by farmer name, user name, message, etc.
    if (q) {
      const query = q.toLowerCase()
      data = data?.filter(
        (el) =>
          el.farmer?.full_name?.toLowerCase().includes(query) ||
          el.farmer?.firstname?.toLowerCase().includes(query) ||
          el.farmer?.lastname?.toLowerCase().includes(query) ||
          el.farmer?.phone?.includes(query) ||
          el.user?.firstname?.toLowerCase().includes(query) ||
          el.user?.lastname?.toLowerCase().includes(query) ||
          el.message?.toLowerCase().includes(query) ||
          el.status?.toLowerCase().includes(query) ||
          el.date?.includes(query)
      )
    }
    return data
  }

  // --- Pagination Logic ---
  const filteredAppointments = getFilteredAppointments()
  const totalPages = Math.ceil(filteredAppointments.length / PAGE_SIZE)
  const paginatedAppointments = filteredAppointments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [filteredAppointments.length])

  // --- Pagination Handlers ---
  const handlePageClick = (page) => {
    setCurrentPage(page)
  }

  const reset = () => {
    setQ("")
    setState({ status: "", date: "" })
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection"
      }
    ])
    setCurrentPage(1)
  }

  function handleChange(evt) {
    const value = evt.target.value
    setState({
      ...states,
      [evt.target.name]: value,
    })
    setCurrentPage(1)
  }

  const { state } = useLocation()
  const user = JSON.parse(localStorage.getItem("user"))
  if (user?.is_firsttime) {
    return <Redirect to={state?.from || "/admin/dashboard"} />
  }

  // Cancel handler
  const handleCancel = async () => {
    if (!selected?.id) return
    setDeleteLoading(true)
    try {
      const token = localStorage.getItem('token')
      await fetch(`${BASE_URL}appointments/${selected.id}/cancel/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setShowDeleteModal(false)
      setDeleteLoading(false)
      mutate()
      setSelected(null)
      SuccessNotification('Appointment cancelled!')
    } catch (err) {
      setDeleteLoading(false)
      setShowDeleteModal(false)
      setSelected(null)
      SuccessNotification(err?.message || 'Failed to cancel appointment')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A'
    return timeString.substring(0, 5) // Convert "14:30:00" to "14:30"
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-50 text-yellow-600'
      case 'confirmed':
        return 'bg-green-50 text-green-600'
      case 'cancelled':
        return 'bg-red-50 text-red-600'
      case 'completed':
        return 'bg-blue-50 text-blue-600'
      default:
        return 'bg-gray-50 text-gray-600'
    }
  }

  return (
    <>
      {showDeleteModal && <DeleteModal
        handleDelete={handleCancel}
        loading={deleteLoading}
        setShowConfirmModal={setShowDeleteModal}
        showConfirmModal={showDeleteModal}
        title={`appointment with ${selected?.farmer?.full_name}`}
      />}

      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          {/* DASHBOARD HEADER */}
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="Appointments" />
          </div>

          {/* FILTER SECTION */}
          <div className="bg-white dark:bg-dark p-4 rounded-lg border border-gray-200 dark:border-border-dark">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search Input */}
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search farmer or user..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-10 py-3 capitalize px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white"
                />
              </div>

              {/* Status Filter */}
              <select
                name="status"
                value={states.status}
                onChange={handleChange}
                className="py-3 capitalize px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>

              {/* Date Filter */}
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  name="date"
                  value={states.date}
                  onChange={handleChange}
                  className="pl-10 py-3 capitalize px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white"
                />
              </div>
            </div>

            {(q || states.status || states.date) && (
              <div className="flex justify-end mt-4">
                <button
                  onClick={reset}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="flex items-center mb-1"></div>
              <div className="my-5">
                {filteredAppointments.length > 0 ? (
                  <>
                    <div className="my-5">
                      <div className="flex flex-col">
                        <div className="-m-1.5 overflow-x-auto">
                          <div className="p-1.5 min-w-full inline-block align-middle">
                            <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                                <thead className="bg-transparent dark:bg-secondaryDark">
                                  <tr>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Farmer</th>                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Date & Time</th>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Message</th>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Status</th>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark">Upcoming</th>
                                    <th className="px-6 py-3 text-end text-sm font-light text-gray-500 capitalize hover:text-black">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                                  {paginatedAppointments.map((data, index) => (
                                    <tr key={data?.id} className="text-[13px]">
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                          <div className="flex-shrink-0 h-10 w-10">
                                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                              {data?.farmer?.profile_image ? (
                                                <img
                                                  src={data?.farmer?.profile_image}
                                                  alt={data?.farmer?.full_name}
                                                  className="h-10 w-10 rounded-full object-cover"
                                                />
                                              ) : (
                                                <span className="text-sm font-medium text-gray-700">
                                                  {data?.farmer?.firstname?.[0]}{data?.farmer?.lastname?.[0]}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                              {data?.farmer?.full_name || `${data?.farmer?.firstname} ${data?.farmer?.lastname}`}
                                            </div>
                                            <div className="capitalize text-xs">
                                              {data?.farmer?.phone}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900 dark:text-white">
                                          {formatDate(data?.date)}
                                        </div>
                                        <div className="capitalize text-xs flex flex-row items-center">
                                          <FiClock className="mr-1" size={12} />
                                          {formatTime(data?.time)}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                                          {data?.message || 'No message'}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`capitalize px-3 py-1 rounded-md text-xs font-semibold ${getStatusColor(data?.status)}`}>
                                          {data?.status}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${data?.is_upcoming
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                          }`}>
                                          {data?.is_upcoming ? 'Yes' : 'No'}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                        <div className="flex flex-row items-center gap-4">
                                          <Tooltip text="View" position="top">
                                            <button
                                              onClick={() => {
                                                history.push(`/admin/appointment/${data?.id}`, { state: data })
                                              }}
                                              className=""
                                            >
                                              <FiEye size={16} />
                                            </button>
                                          </Tooltip>
                                          <Tooltip text="Edit" position="top">
                                            <button onClick={() => {
                                              history.push(`/admin/appointment/${data?.id}/edit`, { state: data })
                                            }}>
                                              <RxPencil1 size={16} />
                                            </button>
                                          </Tooltip>

                                          <Tooltip text="Cancel" position="top">
                                            <button onClick={() => {
                                              setSelected(data)
                                              setShowDeleteModal(true)
                                            }}>
                                              <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              </svg>
                                            </button>
                                          </Tooltip>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageClick}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No appointments found matches the selected filters.</p>
                    <button
                      onClick={reset}
                      className="mt-4 text-yellowcolor hover:text-yellowcolor/80 font-medium"
                    >
                      Reset All Filters
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default AppointmentView